"use client";

import Link from "next/link";
import posthog from "posthog-js";
import { useEffect, useRef } from "react";
import {
  DEADLINE_TEXT_CLASS,
  deadlineInfo,
  locationsSummary,
} from "@/components/format";
import { OrgLogo } from "@/components/OrgLogo";
import { jobPath } from "@/lib/job-url";
import type { RelatedJob } from "@/lib/queries";

/**
 * Related-jobs discovery rail (CM-22) — a tracked mini-product.
 *
 * Relevance is computed server-side (getRelatedJobs); this component's job is
 * the *measurement loop* that lets us improve that baseline systematically:
 *  - `related_jobs_shown` fires once, when the rail actually scrolls into view
 *    (IntersectionObserver — a real impression, so CTR is honest).
 *  - `related_job_clicked` fires per click with the item's rank + matched
 *    signals + score + algorithm version.
 * KPI = click-through, sliced by rank / signal / algorithm_version. That slicing
 * is what turns "did this help?" into a number when we tune the weights.
 *
 * Tracking is consent-gated: PostHog only exists after the user opts in
 * (posthog.__loaded), so all captures are guarded — no consent, no events, and
 * the rail still works.
 */
export function RelatedJobsRail({
  items,
  sourceJobId,
  algorithmVersion,
  sourceFunction,
  sourceOrgLabel,
  now,
}: {
  items: RelatedJob[];
  sourceJobId: number;
  algorithmVersion: string;
  /** The current job's function/org — used to caption the rail + suppress the
   *  redundant per-card label when the whole set shares that signal. */
  sourceFunction?: string | null;
  sourceOrgLabel?: string;
  /** Passed from the server so every card dates against one consistent clock. */
  now: Date;
}) {
  const ref = useRef<HTMLElement | null>(null);
  const shown = useRef(false);

  useEffect(() => {
    if (items.length === 0 || shown.current) return;
    const el = ref.current;
    if (!el) return;
    const fire = () => {
      if (shown.current) return;
      shown.current = true;
      if (posthog.__loaded) {
        posthog.capture("related_jobs_shown", {
          source_job_id: sourceJobId,
          algorithm_version: algorithmVersion,
          count: items.length,
          // Compact per-rank fingerprint so we can attribute impressions to the
          // exact ranked set a click came from (offline CTR-by-rank/signal).
          items: items.map((it, i) => ({
            rank: i + 1,
            job_id: it.job.id,
            score: it.score,
            signals: it.signals,
          })),
        });
      }
    };
    // Real impression: only when ≥30% of the rail is on screen.
    const obs = new IntersectionObserver(
      (entries) => {
        for (const e of entries) if (e.isIntersecting) fire();
      },
      { threshold: 0.3 },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [items, sourceJobId, algorithmVersion]);

  if (items.length === 0) return null;

  // Caption the rail by the signal the set shares, and drop the per-card label
  // when it would just restate that caption (keeps the rail from reading like ten
  // identical tags). A card that matched a *different* way keeps its own label.
  const fnShare =
    items.filter((i) => i.signals.includes("function")).length / items.length;
  const orgShare =
    items.filter((i) => i.signals.includes("org")).length / items.length;
  let heading = "Related vacancies";
  let redundantLabel: string | null = null;
  if (sourceFunction && sourceFunction !== "Other" && fnShare >= 0.6) {
    heading = `More ${sourceFunction} roles`;
    redundantLabel = sourceFunction;
  } else if (sourceOrgLabel && orgShare >= 0.6) {
    heading = `More from ${sourceOrgLabel}`;
    redundantLabel = `More from ${sourceOrgLabel}`;
  }

  return (
    <section ref={ref} aria-label="Related vacancies" className="mt-6">
      <h2 className="telex mb-2 text-[10px] text-neutral-400">{heading}</h2>
      <ul className="space-y-1.5">
        {items.map((it, i) => {
          const job = it.job;
          const dl = deadlineInfo(job.deadline, now);
          const location = locationsSummary(job.locations);
          const rank = i + 1;
          const showReason = it.reason !== redundantLabel;
          return (
            <li key={job.id}>
              <Link
                href={jobPath(job)}
                onClick={() => {
                  if (posthog.__loaded)
                    posthog.capture("related_job_clicked", {
                      source_job_id: sourceJobId,
                      target_job_id: job.id,
                      rank,
                      score: it.score,
                      reason: it.reason,
                      signals: it.signals,
                      algorithm_version: algorithmVersion,
                    });
                }}
                className="group block rounded-lg border border-neutral-200 bg-white p-2.5 transition-colors hover:border-brand-300 hover:bg-brand-50/40"
              >
                {showReason ? (
                  <p className="telex text-[9px] text-brand-700">{it.reason}</p>
                ) : null}
                <div className="mt-1 flex items-start gap-2">
                  <OrgLogo
                    orgKey={job.orgKey}
                    acronym={job.orgAcronym}
                    name={job.orgName}
                    className="mt-0.5 h-7 w-7 shrink-0"
                  />
                  <div className="min-w-0 flex-1">
                    <p className="line-clamp-2 text-[13px] font-medium leading-snug text-ink group-hover:text-brand-700">
                      {job.title}
                    </p>
                    <p className="mt-0.5 truncate text-[11.5px] text-neutral-500">
                      {[job.orgAcronym ?? job.orgName, location]
                        .filter(Boolean)
                        .join(" · ")}
                    </p>
                    {dl ? (
                      <p
                        className={`mt-0.5 text-[11px] font-medium ${DEADLINE_TEXT_CLASS[dl.tone]}`}
                      >
                        {dl.short}
                      </p>
                    ) : null}
                  </div>
                </div>
              </Link>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
